import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Box } from '@mui/material';
import { store, persistor } from './store/store';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { getUserInfo } from './store/actions/user.actions';
import { AuthPage } from './pages/AuthPage';
import { Dashboard } from './pages/Dashboard';
import { ResetPasswordForm } from './components/auth/ResetPasswordForm';
import { getBackgroundImage, getTheme, getUserThemePreferences } from './api/userApi';

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loginUser, isUserLogin, loading } = useAppSelector((state) => state.user);
  const [initializing, setInitializing] = React.useState(true);
  const [backgroundUrl, setBackgroundUrl] = React.useState<string>('/rnm.png');

  useEffect(() => {
    // Initialize auth
    setInitializing(false);

    // Only fetch user info if there's a token
    const hasToken = document.cookie.includes('token=');
    if (hasToken) {
      dispatch(getUserInfo());
    }

    // Load background image for auth pages
    const loadBackground = async () => {
      try {
        const cachedBackground = localStorage.getItem('broodbook-background');
        if (cachedBackground) {
          setBackgroundUrl(cachedBackground);
        }

        const response = await getBackgroundImage();
        if (response?.data?.data?.backgroundUrl) {
          const newBackgroundUrl = response.data.data.backgroundUrl;
          setBackgroundUrl(newBackgroundUrl);
          localStorage.setItem('broodbook-background', newBackgroundUrl);
        }
      } catch (error) {
        console.warn('Failed to load background, using default');
      }
    };

    loadBackground();
  }, [dispatch]);

  // Load theme when user is logged in
  useEffect(() => {
    if (isUserLogin) {
      const loadTheme = async () => {
        try {
          // First check user preferences
          const userPrefsResponse = await getUserThemePreferences();
          const userPrefs = userPrefsResponse.data?.data;

          let theme = null;
          let backgroundUrl = null;

          // If user has personal theme enabled, use it
          if (userPrefs?.usePersonalTheme) {
            if (userPrefs.personalTheme) {
              theme = userPrefs.personalTheme;
            }
            if (userPrefs.personalBackgroundUrl) {
              backgroundUrl = userPrefs.personalBackgroundUrl;
            }
          }

          // Otherwise, load app-wide theme
          if (!theme) {
            // First check JSONP (shared theme)
            if ((window as any).BROODbookThemes && (window as any).BROODbookThemes.current) {
              theme = (window as any).BROODbookThemes.current;
            } else {
              // Fallback to API call if JSONP not available
              const themeResponse = await getTheme();
              if (themeResponse?.data?.data?.theme) {
                theme = themeResponse.data.data.theme;
              }
            }
          }

          if (theme) {
            const root = document.documentElement;

            // Apply colors
            const colorMap: Record<string, string> = {
              'primary': '--primary-color',
              'primaryLight': '--primary-light',
              'primaryDark': '--primary-dark',
              'secondary': '--secondary-color',
              'secondaryLight': '--secondary-light',
              'secondaryDark': '--secondary-dark',
              'backgroundDefault': '--background-default',
              'backgroundPaper': '--background-paper',
              'textPrimary': '--text-primary',
              'textSecondary': '--text-secondary',
              'error': '--error-color',
              'warning': '--warning-color',
              'info': '--info-color',
              'success': '--success-color',
              'borderColor': '--border-color'
            };

            Object.entries(theme.colors).forEach(([key, value]) => {
              const cssVarName = colorMap[key] || `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
              root.style.setProperty(cssVarName, value as string);
            });

            // Apply typography
            root.style.setProperty('--font-family', theme.typography.fontFamily);
            root.style.setProperty('--font-size', theme.typography.fontSize);
            root.style.setProperty('--h1-size', theme.typography.h1Size);
            root.style.setProperty('--h2-size', theme.typography.h2Size);
            root.style.setProperty('--h3-size', theme.typography.h3Size);

            // Apply spacing
            root.style.setProperty('--spacing-unit', theme.spacing.unit);
            root.style.setProperty('--container-padding', theme.spacing.containerPadding);
            root.style.setProperty('--card-margin', theme.spacing.cardMargin);

            // Apply layout
            root.style.setProperty('--border-radius', theme.layout.borderRadius);
            root.style.setProperty('--border-radius-sm', theme.layout.borderRadiusSm);
            root.style.setProperty('--border-radius-lg', theme.layout.borderRadiusLg);

            // Apply effects
            root.style.setProperty('--shadow-light', theme.effects.shadowLight);
            root.style.setProperty('--shadow-medium', theme.effects.shadowMedium);
            root.style.setProperty('--shadow-heavy', theme.effects.shadowHeavy);
          }

          // Apply background (personal or app-wide)
          if (backgroundUrl) {
            // Use personal background
            document.body.style.backgroundImage = `url(${backgroundUrl})`;
            document.body.style.backgroundSize = 'cover';
            document.body.style.backgroundPosition = 'center center';
            document.body.style.backgroundAttachment = 'scroll';
            document.body.style.backgroundRepeat = 'repeat-x';
            document.body.style.backgroundColor = 'black';
            document.body.style.animation = 'moveBackground 60s linear infinite';
          } else if (!userPrefs?.usePersonalTheme) {
            // Load app-wide background only if not using personal theme
            const backgroundResponse = await getBackgroundImage();
            if (backgroundResponse?.data?.data?.backgroundUrl) {
              const appBackgroundUrl = backgroundResponse.data.data.backgroundUrl;
              document.body.style.backgroundImage = `url(${appBackgroundUrl})`;
              document.body.style.backgroundSize = 'cover';
              document.body.style.backgroundPosition = 'center center';
            document.body.style.backgroundAttachment = 'scroll';
            document.body.style.backgroundRepeat = 'repeat-x';
            document.body.style.backgroundColor = 'black';
            document.body.style.animation = 'moveBackground 60s linear infinite';
              localStorage.setItem('broodbook-background', appBackgroundUrl);
            }
          }
        } catch (error) {
          console.warn('Theme/background load failed:', error);
        }
      };

      loadTheme();
    }
  }, [isUserLogin]);

  if (initializing || loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div className="circular-progress">
          <svg width="40" height="40" viewBox="0 0 40 40">
            <circle className="circular-progress-circle" cx="20" cy="20" r="18" fill="none" strokeWidth="4" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/auth"
          element={isUserLogin ? <Navigate to="/dashboard" replace /> : <AuthPage />}
        />
        <Route
          path="/reset-password"
          element={
            <Box
              sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `url(${backgroundUrl}) center center`,
                backgroundRepeat: 'repeat-x',
                backgroundColor: 'black',
                backgroundSize: 'cover',
                animation: 'moveBackground 60s linear infinite',
                '@keyframes moveBackground': {
                  from: { backgroundPosition: '0% 0%' },
                  to: { backgroundPosition: '150% 0%' },
                },
              }}
            >
              <ResetPasswordForm />
            </Box>
          }
        />
        <Route
          path="/dashboard/*"
          element={isUserLogin ? <Dashboard /> : <Navigate to="/auth" replace />}
        />
        <Route
          path="/"
          element={<Navigate to={isUserLogin ? "/dashboard" : "/auth"} replace />}
        />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <Provider store={store}>
      <PersistGate
        loading={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh'
          }}>
            <div className="circular-progress">
              <svg width="40" height="40" viewBox="0 0 40 40">
                <circle className="circular-progress-circle" cx="20" cy="20" r="18" fill="none" strokeWidth="4" />
              </svg>
            </div>
          </div>
        }
        persistor={persistor}
      >
        <AppContent />
      </PersistGate>
    </Provider>
  );
}

export default App;